import { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  Calculator,
  Database,
  Grid3X3,
  History,
  Settings,
  LogOut,
  Menu,
  FolderOpen,
  Layers,
  GitCompareArrows,
  Users,
  Scale,
  Package,
  Store,
  PackageOpen,
  Scissors,
  Upload as UploadIcon,
} from "lucide-react";

const LOGO_IMG = "https://customer-assets.emergentagent.com/job_yarn-weight-pricer/artifacts/6sx1m0su_WhatsApp%20Image%202026-04-13%20at%207.20.45%20PM.jpeg";

const navItems = [
  { to: "/calculator", label: "Cost Calculator", icon: Calculator },
  { to: "/combo", label: "Combo Calculator", icon: PackageOpen },
  { to: "/designs", label: "Saved Designs", icon: FolderOpen },
  { to: "/compare", label: "Compare Designs", icon: GitCompareArrows },
  { to: "/weavers", label: "Weaver Master", icon: Users },
  { to: "/weaver-compare", label: "Supplier Comparison", icon: Scale },
  { to: "/item-master", label: "Item Master", icon: Package },
  { to: "/vendors", label: "Vendor Master", icon: Store },
  { to: "/trims-master", label: "Trims Master", icon: Scissors },
  { to: "/bulk-import", label: "Bulk Import", icon: UploadIcon },
  { to: "/yarn-master", label: "Yarn Master", icon: Database },
  { to: "/border-master", label: "Border Master", icon: Grid3X3 },
  { to: "/processing-master", label: "Processing Master", icon: Layers },
  { to: "/history", label: "History", icon: History },
  { to: "/settings", label: "Settings", icon: Settings },
];

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <div className="flex flex-col h-full">
      <div className="p-5 pb-4 flex justify-center">
        <img src={LOGO_IMG} alt="Logo" className="h-12" />
      </div>
      <Separator />
      <nav className="flex-1 p-3 space-y-1" data-testid="sidebar-nav">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[#C2410C]/10 text-[#C2410C]"
                  : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"
              }`
            }
            data-testid={`nav-${to.replace("/", "")}`}
          >
            <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
            {label}
          </NavLink>
        ))}
      </nav>
      <Separator />
      <div className="p-4 space-y-3">
        <div className="text-xs text-muted-foreground truncate">
          {user?.email}
        </div>
        <Button
          variant="ghost"
          size="sm"
          data-testid="logout-button"
          className="w-full justify-start text-stone-600 hover:text-red-600 hover:bg-red-50"
          onClick={logout}
        >
          <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Sign out
        </Button>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const location = useLocation();

  const currentPage = navItems.find((n) => location.pathname.startsWith(n.to));

  return (
    <div className="min-h-screen flex bg-background" data-testid="dashboard-layout">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-56 lg:w-60 flex-col border-r border-stone-200 bg-white shrink-0 sticky top-0 h-screen">
        <SidebarContent onNavigate={() => {}} />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-white/80 backdrop-blur-xl sticky top-0 z-50">
          <div className="flex items-center gap-3">
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64">
                <SidebarContent onNavigate={() => setSheetOpen(false)} />
              </SheetContent>
            </Sheet>
            <span className="font-heading font-medium text-sm text-foreground">
              {currentPage?.label || "Dashboard"}
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-6xl w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
